import { FormEntry } from "../../../types/types";
import { formMappings } from "../../../config/formMappings";
import { formFields } from "../../../services/form_fields";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

export default function SurveyResponseCard(props: {
  entry: FormEntry;
  className: string;
}) {
  // Get form mapping from Redux state
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";

  // Get phone field key from formMappings
  const phoneFieldKey = formMapping?.fieldMappings?.phone;

  // For v1, the API service maps field keys to human-readable names
  // For v2, the field keys remain as-is (if not in form_fields.ts)
  let phoneNumber = "";
  if (phoneFieldKey) {
    // Check if API service mapped it to human-readable name
    const humanReadableName = formFields[phoneFieldKey]?.name;
    if (humanReadableName && props.entry.meta[humanReadableName]) {
      // v1: Use human-readable name (API service mapped it)
      phoneNumber = props.entry.meta[humanReadableName] || "";
    } else {
      // v2: Use raw field key (not mapped by API service)
      phoneNumber = props.entry.meta[phoneFieldKey] || "";
    }
  }

  // Get location (city and state) from formMappings
  const cityFieldKey = formMapping?.fieldMappings?.city;
  const stateFieldKey = formMapping?.fieldMappings?.state;
  const addressObjectKey = (formMapping?.fieldMappings as any)?.address_object;

  let location = "";
  if (isVersion1) {
    // Version 1: Get city and state from address_object
    if (addressObjectKey && props.entry.meta[addressObjectKey]) {
      const addressObject = props.entry.meta[addressObjectKey] as any;
      if (addressObject && typeof addressObject === "object") {
        const city = addressObject.city || "";
        const state = addressObject.state || "";
        if (city && state) {
          location = `${city}, ${state}`;
        } else if (city) {
          location = city;
        } else if (state) {
          location = state;
        }
      }
    }
  } else {
    // Version 2+: Get city and state directly from mapped fields
    let city = "";
    let state = "";

    if (cityFieldKey) {
      const humanReadableName = formFields[cityFieldKey]?.name;
      if (humanReadableName && props.entry.meta[humanReadableName]) {
        city = props.entry.meta[humanReadableName] || "";
      } else {
        const cityValue = props.entry.meta[cityFieldKey];
        if (Array.isArray(cityValue)) {
          city = cityValue[0] || "";
        } else if (typeof cityValue === "string") {
          city = cityValue;
        } else if (cityValue) {
          city = String(cityValue);
        }
      }
    }

    if (stateFieldKey) {
      const humanReadableName = formFields[stateFieldKey]?.name;
      if (humanReadableName && props.entry.meta[humanReadableName]) {
        state = props.entry.meta[humanReadableName] || "";
      } else {
        const stateValue = props.entry.meta[stateFieldKey];
        if (Array.isArray(stateValue)) {
          state = stateValue[0] || "";
        } else if (typeof stateValue === "string") {
          state = stateValue;
        } else if (stateValue) {
          state = String(stateValue);
        }
      }
    }

    if (city && state) {
      location = `${city}, ${state}`;
    } else if (city) {
      location = city;
    } else if (state) {
      location = state;
    }
  }

  return (
    <div
      className={`overflow-hidden bg-white shadow rounded-lg col-span-2 ${props.className}`}
    >
      <div className="px-4 py-6 sm:px-6">
        <h3 className="text-base/7 font-semibold text-gray-900">
          General Info
        </h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          General Information of the group.
        </p>
      </div>
      <div className="border-t border-gray-100">
        <dl className="divide-y divide-gray-100">
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">Phone Number</dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {phoneNumber ? (
                <a href={`tel:${phoneNumber}`} target="_blank" rel="noreferrer">
                  {phoneNumber}
                </a>
              ) : (
                ""
              )}
            </dd>
          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">Location</dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {location || ""}
            </dd>
          </div>
          {isVersion1 && (
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-900">Ethnicity</dt>
              <dd className="mt-1 text-md/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {Array.isArray(props.entry.meta["Ethnicity"]) ? (
                  props.entry.meta["Ethnicity"].map((ethnicity, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 mr-3 text-md font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"
                    >
                      {ethnicity}
                    </span>
                  ))
                ) : (
                  <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-md font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                    {props.entry.meta["Ethnicity"]}
                  </span>
                )}
              </dd>
            </div>
          )}
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">
              Staying at Hotel?
            </dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {(() => {
                // Get staying in hotel field key from formMappings
                const stayingInHotelFieldKey =
                  formMapping?.fieldMappings?.["Staying in Hotel"];

                let stayingInHotel = "";
                if (stayingInHotelFieldKey) {
                  // Check if API service mapped it to human-readable name
                  const humanReadableName =
                    formFields[stayingInHotelFieldKey]?.name;
                  if (
                    humanReadableName &&
                    props.entry.meta[humanReadableName]
                  ) {
                    // v1: Use human-readable name (API service mapped it)
                    stayingInHotel = props.entry.meta[humanReadableName] || "";
                  } else {
                    // v2: Use raw field key (not mapped by API service)
                    stayingInHotel =
                      props.entry.meta[stayingInHotelFieldKey] || "";
                  }
                }
                return stayingInHotel;
              })()}
            </dd>
          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">
              Last Attendance
            </dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {(() => {
                // Get last time attended field key from formMappings
                const lastTimeAttendedFieldKey =
                  formMapping?.fieldMappings?.["Last Time Attended"];

                let lastTimeAttended = "";
                if (lastTimeAttendedFieldKey) {
                  // Check if API service mapped it to human-readable name
                  const humanReadableName =
                    formFields[lastTimeAttendedFieldKey]?.name;
                  if (
                    humanReadableName &&
                    props.entry.meta[humanReadableName]
                  ) {
                    // v1: Use human-readable name (API service mapped it)
                    lastTimeAttended =
                      props.entry.meta[humanReadableName] || "";
                  } else {
                    // v2: Use raw field key (not mapped by API service)
                    lastTimeAttended =
                      props.entry.meta[lastTimeAttendedFieldKey] || "";
                  }
                }
                return lastTimeAttended;
              })()}
            </dd>
          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">
              Heard About Conference
            </dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {(() => {
                // Get hear about conference field key from formMappings
                const hearAboutConferenceFieldKey =
                  formMapping?.fieldMappings?.["Hear about Conference"];

                let hearAboutConference = "";
                if (hearAboutConferenceFieldKey) {
                  // Check if API service mapped it to human-readable name
                  const humanReadableName =
                    formFields[hearAboutConferenceFieldKey]?.name;
                  if (
                    humanReadableName &&
                    props.entry.meta[humanReadableName]
                  ) {
                    // v1: Use human-readable name (API service mapped it)
                    hearAboutConference =
                      props.entry.meta[humanReadableName] || "";
                  } else {
                    // v2: Use raw field key (not mapped by API service)
                    hearAboutConference =
                      props.entry.meta[hearAboutConferenceFieldKey] || "";
                  }
                }
                return hearAboutConference;
              })()}
            </dd>
          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">
              Getting to Conference by
            </dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {(() => {
                // Get getting to conference field key from formMappings
                const gettingToConferenceFieldKey =
                  formMapping?.fieldMappings?.["Getting to Conference by"];

                let gettingToConference = "";
                if (gettingToConferenceFieldKey) {
                  // Check if API service mapped it to human-readable name
                  const humanReadableName =
                    formFields[gettingToConferenceFieldKey]?.name;
                  if (
                    humanReadableName &&
                    props.entry.meta[humanReadableName]
                  ) {
                    // v1: Use human-readable name (API service mapped it)
                    gettingToConference =
                      props.entry.meta[humanReadableName] || "";
                  } else {
                    // v2: Use raw field key (not mapped by API service)
                    gettingToConference =
                      props.entry.meta[gettingToConferenceFieldKey] || "";
                  }
                }
                return gettingToConference;
              })()}
            </dd>
          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">
              Most Active Social Media
            </dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {(() => {
                // Get most active social media field key from formMappings
                const mostActiveSocialMediaFieldKey =
                  formMapping?.fieldMappings?.["Most Active Social Media"];

                let mostActiveSocialMedia = "";
                if (mostActiveSocialMediaFieldKey) {
                  // Check if API service mapped it to human-readable name
                  const humanReadableName =
                    formFields[mostActiveSocialMediaFieldKey]?.name;
                  if (
                    humanReadableName &&
                    props.entry.meta[humanReadableName]
                  ) {
                    // v1: Use human-readable name (API service mapped it)
                    mostActiveSocialMedia =
                      props.entry.meta[humanReadableName] || "";
                  } else {
                    // v2: Use raw field key (not mapped by API service)
                    mostActiveSocialMedia =
                      props.entry.meta[mostActiveSocialMediaFieldKey] || "";
                  }
                }
                return mostActiveSocialMedia;
              })()}
            </dd>
          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">
              Food Allergies
            </dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {(() => {
                // Get food allergies field key from formMappings
                const foodAllergiesFieldKey =
                  formMapping?.fieldMappings?.["Food Allergies"];

                let foodAllergies = "";
                if (foodAllergiesFieldKey) {
                  // Check if API service mapped it to human-readable name
                  const humanReadableName =
                    formFields[foodAllergiesFieldKey]?.name;
                  if (
                    humanReadableName &&
                    props.entry.meta[humanReadableName]
                  ) {
                    // v1: Use human-readable name (API service mapped it)
                    foodAllergies = props.entry.meta[humanReadableName] || "";
                  } else {
                    // v2: Use raw field key (not mapped by API service)
                    foodAllergies =
                      props.entry.meta[foodAllergiesFieldKey] || "";
                  }
                }
                return foodAllergies ? (
                  foodAllergies
                ) : (
                  <p className="italic">None</p>
                );
              })()}
            </dd>
          </div>
          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-900">
              Special Accommodations
            </dt>
            <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
              {(() => {
                // Get special accommodations field key from formMappings
                const specialAccommodationsFieldKey =
                  formMapping?.fieldMappings?.["Special Accommodations"];

                let specialAccommodations = "";
                if (specialAccommodationsFieldKey) {
                  // Check if API service mapped it to human-readable name
                  const humanReadableName =
                    formFields[specialAccommodationsFieldKey]?.name;
                  if (
                    humanReadableName &&
                    props.entry.meta[humanReadableName]
                  ) {
                    // v1: Use human-readable name (API service mapped it)
                    specialAccommodations =
                      props.entry.meta[humanReadableName] || "";
                  } else {
                    // v2: Use raw field key (not mapped by API service)
                    specialAccommodations =
                      props.entry.meta[specialAccommodationsFieldKey] || "";
                  }
                }
                return specialAccommodations ? (
                  specialAccommodations
                ) : (
                  <p className="italic">None</p>
                );
              })()}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
